import React, { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import Navigation from '../components/common/Navigation'
import RiskDisclaimerPopup from '../components/common/RiskDisclaimerPopup'
import logoIcon from '../assets/logo-icon.png'
import { hasAcceptedRiskDisclaimer, setRiskDisclaimerAccepted } from '../utils/riskDisclaimer'

const PublicLayout: React.FC = () => {
  const [showRiskDisclaimer, setShowRiskDisclaimer] = useState(false)

  useEffect(() => {
    // Check if user has already accepted the risk disclaimer
    const hasAccepted = hasAcceptedRiskDisclaimer()
    if (!hasAccepted) {
      // Show popup after a short delay for better UX
      const timer = setTimeout(() => {
        setShowRiskDisclaimer(true)
      }, 1000)
      
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptDisclaimer = () => {
    setRiskDisclaimerAccepted(true)
    setShowRiskDisclaimer(false)
  }

  const handleDeclineDisclaimer = () => {
    setShowRiskDisclaimer(false)
    // Optionally, you could redirect users away or show a message
    // For now, we'll just close the popup but not save acceptance
  }

  return (
    <div className="min-h-screen bg-dark-950">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary-600 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to main content
      </a>
      <Navigation />

      <main id="main-content" tabIndex={-1}>
        <Outlet />
      </main>
      
      {/* Risk Disclaimer Popup */}
      <RiskDisclaimerPopup 
        isOpen={showRiskDisclaimer}
        onAccept={handleAcceptDisclaimer}
        onDecline={handleDeclineDisclaimer}
      />
      
      <footer className="bg-dark-900 text-gray-100 border-t border-dark-700">

        {/* SEBI Registration Banner */}
        <div className="bg-dark-800 border-b border-dark-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-gray-400 text-center">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>
                <strong className="text-gray-200">SEBI Registered Research Analyst</strong>
              </span>
              <span>Reg. No. <strong className="text-gray-200">INH000023719</strong></span>
              <span>Type: <strong className="text-gray-200">Individual</strong></span>
              <span>BSE RAASB Enlistment No. <strong className="text-gray-200">7041</strong></span>
              <span className="hidden sm:inline">|</span>
              <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline underline-offset-2">
                SCORES Grievance Portal<span className="sr-only"> (opens in a new tab)</span>
              </a>
              <span aria-hidden="true" className="hidden sm:inline">|</span>
              <a href="https://smartodr.in" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline underline-offset-2">
                ODR Portal<span className="sr-only"> (opens in a new tab)</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Footer Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8">

            {/* Brand + SEBI Card */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-3">
                <img src={logoIcon} alt="" className="w-8 h-8 object-contain" />
                <h3 className="text-lg font-bold text-white">AnalyzerView</h3>
              </div>
              <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                SEBI registered research analyst providing equity research, stock recommendations,
                and market analysis. Advisory scope only — not a portfolio manager or investment advisor.
              </p>

              {/* SEBI Registration Card */}
              <div className="bg-dark-800 border border-dark-600 rounded-xl p-4 space-y-2 text-xs">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-green-400"></span>
                  <span className="font-semibold text-gray-200 uppercase tracking-wide text-[10px]">SEBI Registration</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Reg. Number</span>
                  <span className="text-gray-200 font-mono font-semibold">INH000023719</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Category</span>
                  <span className="text-gray-300">Research Analyst (Individual)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">BSE RAASB Enlistment</span>
                  <span className="text-gray-300">7041</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Framework</span>
                  <span className="text-gray-300">RA Regulations, 2014</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Compliance</span>
                  <span className="text-green-400 font-medium">Active</span>
                </div>
                <div className="pt-2 border-t border-dark-600">
                  <span className="text-gray-400">Compliance Officer</span>
                  <p className="text-gray-300 mt-0.5">Rahul Das</p>
                  <a href="mailto:compliance@analyzerview.com" className="text-primary-400 hover:text-primary-300 text-[11px]">
                    compliance@analyzerview.com
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white uppercase tracking-wide">Quick Links</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><a href="/about" className="hover:text-primary-400 transition-colors">About Us</a></li>
                <li><a href="/pricing" className="hover:text-primary-400 transition-colors">Pricing & Plans</a></li>
                <li><a href="/contact" className="hover:text-primary-400 transition-colors">Contact Us</a></li>
                <li><a href="/login" className="hover:text-primary-400 transition-colors">Client Login</a></li>
                <li><a href="/register" className="hover:text-primary-400 transition-colors">Get Started</a></li>
              </ul>
            </div>

            {/* SEBI Compliance */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white uppercase tracking-wide">SEBI Compliance</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><a href="/investor-charter" className="hover:text-primary-400 transition-colors">Investor Charter</a></li>
                <li><a href="/risk-disclosure" className="hover:text-primary-400 transition-colors">Risk Disclosure</a></li>
                <li><a href="/code-of-conduct" className="hover:text-primary-400 transition-colors">Code of Conduct</a></li>
                <li><a href="/mitc" className="hover:text-primary-400 transition-colors">MITC</a></li>
                <li><a href="/details-of-complaint" className="hover:text-primary-400 transition-colors">Complaint Details</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white uppercase tracking-wide">Legal</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-primary-400 transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-primary-400 transition-colors">Privacy Policy</a></li>
                <li><a href="/risk-disclosure" className="hover:text-primary-400 transition-colors">Disclaimer</a></li>
                <li><a href="/refund" className="hover:text-primary-400 transition-colors">Refund Policy</a></li>
              </ul>
            </div>

            {/* Investor Resources */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white uppercase tracking-wide">Investor Help</h4>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li>
                  <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                    SCORES Portal
                    <svg aria-hidden="true" focusable="false" className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg><span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </li>
                <li>
                  <a href="https://smartodr.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                    ODR Portal
                    <svg aria-hidden="true" focusable="false" className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg><span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </li>
                <li>
                  <a href="https://investor.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                    SEBI Investor Ed.
                    <svg aria-hidden="true" focusable="false" className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg><span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </li>
                <li>
                  <a href="https://www.bseindia.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                    BSE India
                    <svg aria-hidden="true" focusable="false" className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg><span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </li>
                <li>
                  <a href="https://www.nseindia.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors flex items-center gap-1">
                    NSE India
                    <svg aria-hidden="true" focusable="false" className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg><span className="sr-only"> (opens in a new tab)</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Regulatory Disclosures Block */}
        <div className="border-t border-dark-700 bg-dark-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Important Regulatory Disclosures</p>
            <div className="grid md:grid-cols-2 gap-4 text-xs text-gray-400 leading-relaxed">
              <div className="space-y-3">
                <p>
                  <strong className="text-gray-400">Risk Disclaimer:</strong> Investments in securities market are subject to market risks.
                  Read all the related documents carefully before investing. Past performance is not indicative of future returns.
                  There is no assurance or guarantee of the returns. Please consider your specific investment requirements,
                  risk tolerance, goal, time frame, risk and reward balance before investing in securities markets.
                </p>
                <p>
                  <strong className="text-gray-400">Research Analyst Disclosure:</strong> AnalyzerView (SEBI Reg. INH000023719) is registered
                  as a Research Analyst under SEBI (Research Analysts) Regulations, 2014. We are not a portfolio management
                  service or investment advisor. Research reports and stock recommendations are for informational purposes only
                  and do not constitute investment advice.
                </p>
              </div>
              <div className="space-y-3">
                <p>
                  <strong className="text-gray-400">Conflict of Interest:</strong> The Research Analyst or his/her associates or relatives
                  may have financial interest, actual/beneficial ownership, or other material conflict of interest in the subject company.
                  Research Analyst or associates may have received compensation from the subject company in the past 12 months.
                  Clients should make their own independent evaluation before investing.
                </p>
                <p>
                  <strong className="text-gray-400">Grievance Redressal:</strong> For complaints, contact our Compliance Officer Rahul Das at{' '}
                  <a href="mailto:compliance@analyzerview.com" className="text-primary-400 hover:text-primary-300">compliance@analyzerview.com</a>.
                  If not resolved within 30 days, escalate via{' '}
                  <a href="https://scores.sebi.gov.in" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300">SCORES</a> or{' '}
                  <a href="https://smartodr.in" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300">ODR Portal</a>.
                  SEBI Helpline: 1800 266 7575 (Toll Free). SEBI Office: SEBI Bhavan, Plot No. C4-A, G Block, Bandra Kurla Complex, Mumbai – 400051.
                </p>
              </div>
            </div>

            {/* Compliance badges row */}
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                'SEBI RA Reg. INH000023719',
                'BSE RAASB Enlistment No. 7041',
                'NISM Certified',
                'RA Regulations 2014',
                'Fixed Fee Model',
                'Advisory Scope Only',
              ].map(tag => (
                <span key={tag} className="text-[10px] px-2.5 py-1 bg-dark-700 text-gray-400 border border-dark-600 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-dark-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400">
            <p>&copy; {new Date().getFullYear()} AnalyzerView. All rights reserved. SEBI Reg. No. INH000023719.</p>
          </div>
        </div>

      </footer>
    </div>
  )
}

export default PublicLayout
